// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type { Coverage, Organization } from '@medplum/fhirtypes';
import { COVERAGE_CLASS_CODES, PATIENT_EXTENSION_URLS } from '../constants/patient-profile';

export function getCoverageClassValue(coverage: Coverage, type: string): string {
  return coverage.class?.find((c) => c.type?.coding?.[0]?.code === type)?.value ?? '';
}

export function getCoverageClassName(coverage: Coverage, type: string): string {
  return coverage.class?.find((c) => c.type?.coding?.[0]?.code === type)?.name ?? '';
}

export function setCoverageClass(coverage: Coverage, type: string, value: string, name?: string): Coverage {
  const classes = [...(coverage.class ?? [])];
  const index = classes.findIndex((c) => c.type?.coding?.[0]?.code === type);
  if (!value) {
    if (index >= 0) {
      classes.splice(index, 1);
    }
    return { ...coverage, class: classes };
  }
  const entry = {
    type: { coding: [{ code: type }] },
    value,
    ...(name !== undefined ? { name } : {}),
  };
  if (index >= 0) {
    classes[index] = entry;
  } else {
    classes.push(entry);
  }
  return { ...coverage, class: classes };
}

export function getCoveragePhone(coverage: Coverage): string {
  const ext = coverage.extension?.find((e) => e.url === PATIENT_EXTENSION_URLS.coveragePhone);
  return ext?.valueString ?? '';
}

export function setCoveragePhone(coverage: Coverage, phone: string): Coverage {
  const extensions = (coverage.extension ?? []).filter((e) => e.url !== PATIENT_EXTENSION_URLS.coveragePhone);
  if (phone) {
    extensions.push({ url: PATIENT_EXTENSION_URLS.coveragePhone, valueString: phone });
  }
  return { ...coverage, extension: extensions };
}

export function getPlanName(coverage: Coverage): string {
  return getCoverageClassValue(coverage, COVERAGE_CLASS_CODES.plan);
}

export function setPlanName(coverage: Coverage, name: string): Coverage {
  return setCoverageClass(coverage, COVERAGE_CLASS_CODES.plan, name, name || undefined);
}

export function getGroupId(coverage: Coverage): string {
  return getCoverageClassValue(coverage, COVERAGE_CLASS_CODES.group);
}

export function setGroupId(coverage: Coverage, groupId: string): Coverage {
  return setCoverageClass(coverage, COVERAGE_CLASS_CODES.group, groupId);
}

/** Resolves the payor Organization referenced by Coverage.payor[0], if any. */
export async function resolvePayorOrganization(
  medplum: MedplumClient,
  coverage: Coverage
): Promise<Organization | undefined> {
  const payorRef = coverage.payor?.[0]?.reference;
  if (!payorRef?.startsWith('Organization/')) {
    return undefined;
  }
  try {
    return await medplum.readReference<Organization>({ reference: payorRef });
  } catch {
    return undefined;
  }
}

/**
 * Returns the most recent active Coverage for a patient, or any Coverage if no active exists.
 * Profile-2 treats this as "primary" coverage; secondary coverage is out of scope.
 */
export async function fetchPrimaryCoverage(medplum: MedplumClient, patientId: string): Promise<Coverage | undefined> {
  const active = await medplum.searchOne('Coverage', {
    beneficiary: `Patient/${patientId}`,
    status: 'active',
    _sort: '-_lastUpdated',
  });
  if (active) {
    return active;
  }
  return medplum.searchOne('Coverage', {
    beneficiary: `Patient/${patientId}`,
    _sort: '-_lastUpdated',
  });
}

export async function fetchSecondaryCoverage(
  medplum: MedplumClient,
  patientId: string,
  primaryId?: string
): Promise<Coverage | undefined> {
  const coverages = await medplum.searchResources('Coverage', {
    beneficiary: `Patient/${patientId}`,
    status: 'active',
    _sort: '-_lastUpdated',
  });
  // The primary is the most recently updated active Coverage, which can itself carry order 2; never return it twice.
  const others = (coverages as Coverage[]).filter((candidate) => candidate.id !== primaryId);
  return others.find((candidate) => candidate.order === 2) ?? others[0];
}
